import React, { useEffect, useState } from "react";
import Input from "../../../components/FormComponents/Input";
import Button from "../../../components/FormComponents/Button";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import Toggle from "../../../components/FormComponents/Toggle";
import ActivityJobTrigger from "../../StateMachine/JobTrigger/ActivityJobTrigger";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { useModalContext } from "../../../context/ModalContext";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import { getToolTipAttributes } from "../../../utils/tooltip";

export const WebhookAPIKeyGeneratorLatest = ({ id, apiKeys = {}, handleInputChange }) => {
  const { getUserConfirmation } = useModalContext();
  const [webhookAPIKeys, setWebhookAPIKeys] = useState(apiKeys);
  const [newKeyName, setNewKeyName] = useState("");

  // Update parent state whenever webhookAPIKeys changes
  const updateParentState = (updatedKeys) => {
    if (handleInputChange) {
      handleInputChange("api_keys", updatedKeys);
    }
  };

  const generateAPIKey = async (keyName) => {
    try {
      let userConfirmed = true;
      if (webhookAPIKeys[keyName]) {
        userConfirmed = await getUserConfirmation(`you want to regenerate API key for <Label>${keyName}</Label>?`);
      }
      if (userConfirmed) {
        const { data } = await axiosInstance.get(`/webhook-config/api-key/${id}`);
        const updatedKeys = {
          ...webhookAPIKeys,
          [keyName]: data.api_key
        };
        setWebhookAPIKeys(updatedKeys);
        updateParentState(updatedKeys);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const addNewAPIKey = async () => {
    if (!newKeyName.trim()) {
      alert("Please enter a key name");
      return;
    }

    if (webhookAPIKeys[newKeyName]) {
      alert("Key name already exists");
      return;
    }

    await generateAPIKey(newKeyName);
    setNewKeyName("");
  };

  const deleteAPIKey = async (keyName) => {
    const userConfirmed = await getUserConfirmation(`you want to delete API key <Label>${keyName}</Label>?`);
    if (userConfirmed) {
      const updatedKeys = { ...webhookAPIKeys };
      delete updatedKeys[keyName];
      setWebhookAPIKeys(updatedKeys);
      updateParentState(updatedKeys);
    }
  };

  const handleCopy = (copyString) => {
    navigator.clipboard
      .writeText(copyString)
      .then(() => {
        toastSuccess("API key copied to clipboard");
      })
      .catch(() => {
        toastError("Failed to copy API key");
      });
  };

  const apiKeyEntries = Object.entries(webhookAPIKeys);

  return (
    <div className="d_flex flex_column gap_3 w_100">
      <div className="d_flex gap_2 align_center w_50">
        <Input
          placeholder="Enter key name"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && addNewAPIKey()}
        />
        <Button variant="secondary" onClick={addNewAPIKey} className="btn btn_primary">
          Add API Key
        </Button>
      </div>

      <div className="d_flex flex_column gap_2 w_100">
        {apiKeyEntries.length === 0 ? (
          <p className="m_0 text_muted">No API keys generated</p>
        ) : (
          apiKeyEntries.map(([keyName, apiKey]) => (
            <div key={keyName} className="d_flex gap_2 align_center">
              <div className="d_flex align_center gap_1 flex_1 border p_2 w_25">
                <div className="d_flex align_center gap_1 w_100">
                  <strong className="font_weight_medium overflow_hidden">{keyName}</strong>
                </div>
              </div>

              <div className="border d_flex p_2 gap_2 align_center flex_1 w_fit">
                <p className="m_0 truncate_text">{apiKey || "No auth"}</p>
                <MaterialIcon
                  name="content_copy"
                  style={{ fontSize: "20px" }}
                  className="cursor_pointer"
                  onClick={() => handleCopy(apiKey)}
                />
              </div>
              <div className="d_flex gap_1 align_center w_25">
                <MaterialIcon
                  name="delete"
                  className="cursor_pointer"
                  style={{
                    color: "#dc3545",
                    fontSize: "20px"
                  }}
                  onClick={() => deleteAPIKey(keyName)}
                  title="Delete API Key"
                  {...getToolTipAttributes("Delete Key", "bottom")}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export function QueryParamsTab({ selectedApiData, handleInputChange }) {
  const [newKey, setNewKey] = useState("");
  const [editedKeys, setEditedKeys] = useState({});

  const currentParams = selectedApiData.query_params || [];

  const handleAddKey = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || currentParams.includes(trimmedKey)) return;

    const updatedParams = [...currentParams, trimmedKey];
    handleInputChange("query_params", updatedParams);
    setNewKey("");
  };

  const handleKeyChange = (oldKey, newKey) => {
    setEditedKeys((prev) => ({ ...prev, [oldKey]: newKey }));
  };

  const handleKeyBlur = (oldKey) => {
    const newKey = editedKeys[oldKey]?.trim();
    if (!newKey || newKey === oldKey || currentParams.includes(newKey)) {
      setEditedKeys((prev) => {
        const copy = { ...prev };
        delete copy[oldKey];
        return copy;
      });
      return;
    }

    const updatedParams = currentParams.map((key) => (key === oldKey ? newKey : key));

    handleInputChange("query_params", updatedParams);
    setEditedKeys((prev) => {
      const copy = { ...prev };
      delete copy[oldKey];
      return copy;
    });
  };

  const handleDeleteKey = (keyToDelete) => {
    const updatedParams = currentParams.filter((key) => key !== keyToDelete);
    handleInputChange("query_params", updatedParams);
  };

  return (
    <div className="w_50 mt_3">
      <div className="d_flex gap_2 mb_2 mt_2">
        <Input placeholder="Enter key" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
        <Button onClick={handleAddKey}>+ Add Query Param</Button>
      </div>

      <div className="mt_4">
        <ul className="d_flex flex_column gap_2">
          {currentParams.map((key) => (
            <li key={key} className="d_flex align_center gap_2">
              <Input
                value={editedKeys[key] ?? key}
                onChange={(e) => handleKeyChange(key, e.target.value)}
                onBlur={() => handleKeyBlur(key)}
                className="flex_1"
              />
              <MaterialIcon
                name="delete"
                className="cursor_pointer"
                style={{
                  color: "#dc3545",
                  fontSize: "20px"
                }}
                onClick={() => handleDeleteKey(key)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function PathParamsTab({ selectedApiData, handleInputChange }) {
  const [newKey, setNewKey] = useState("");
  const [editedKeys, setEditedKeys] = useState({});

  const currentParams = selectedApiData.path_params || [];

  const handleAddKey = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || currentParams.includes(trimmedKey)) return;

    const updatedParams = [...currentParams, trimmedKey];
    handleInputChange("path_params", updatedParams);
    setNewKey("");
  };

  const handleKeyChange = (oldKey, newKey) => {
    setEditedKeys((prev) => ({ ...prev, [oldKey]: newKey }));
  };

  const handleKeyBlur = (oldKey) => {
    const newKey = editedKeys[oldKey]?.trim();
    if (!newKey || newKey === oldKey || currentParams.includes(newKey)) {
      setEditedKeys((prev) => {
        const copy = { ...prev };
        delete copy[oldKey];
        return copy;
      });
      return;
    }

    const updatedParams = currentParams.map((key) => (key === oldKey ? newKey : key));

    handleInputChange("path_params", updatedParams);
    setEditedKeys((prev) => {
      const copy = { ...prev };
      delete copy[oldKey];
      return copy;
    });
  };

  const handleDeleteKey = (keyToDelete) => {
    const updatedParams = currentParams.filter((key) => key !== keyToDelete);
    handleInputChange("path_params", updatedParams);
  };

  return (
    <div className="w_50 mt_3">
      <div className="d_flex gap_2 mb_2 mt_2">
        <Input placeholder="Enter key" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
        <Button onClick={handleAddKey}>+ Add Path Param</Button>
      </div>

      <div className="mt_4">
        <ul className="d_flex flex_column gap_2">
          {currentParams.map((key) => (
            <li key={key} className="d_flex align_center gap_2">
              <Input
                value={editedKeys[key] ?? key}
                onChange={(e) => handleKeyChange(key, e.target.value)}
                onBlur={() => handleKeyBlur(key)}
                className="flex_1"
              />
              <MaterialIcon
                name="delete"
                className="cursor_pointer"
                style={{
                  color: "#dc3545",
                  fontSize: "20px"
                }}
                onClick={() => handleDeleteKey(key)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function BodyParamsTab({ selectedApiData, handleInputChange }) {
  const [newKey, setNewKey] = useState("");
  const [editedKeys, setEditedKeys] = useState({});
  const [copied, setCopied] = useState(false);

  const currentParams = selectedApiData.body_params || {};

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(currentParams, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleAddKey = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || currentParams[trimmedKey]) return;

    const updatedParams = {
      ...currentParams,
      [trimmedKey]: ""
    };

    handleInputChange("body_params", updatedParams);
    setNewKey("");
  };

  const handleKeyChange = (oldKey, newKey) => {
    setEditedKeys((prev) => ({ ...prev, [oldKey]: newKey }));
  };

  const handleKeyBlur = (oldKey) => {
    const newKey = editedKeys[oldKey]?.trim();
    if (!newKey || newKey === oldKey || currentParams[newKey]) {
      setEditedKeys((prev) => {
        const copy = { ...prev };
        delete copy[oldKey];
        return copy;
      });
      return;
    }

    const { [oldKey]: value, ...rest } = currentParams;
    const updatedParams = {
      ...rest,
      [newKey]: value
    };

    handleInputChange("body_params", updatedParams);
    setEditedKeys((prev) => {
      const copy = { ...prev };
      delete copy[oldKey];
      return copy;
    });
  };

  const handleDeleteKey = (keyToDelete) => {
    const updatedParams = { ...currentParams };
    delete updatedParams[keyToDelete];
    handleInputChange("body_params", updatedParams);
  };

  return (
    <div className="w_100 d_flex gap_2 mt_3">
      <div className="w_50">
        <div className="d_flex gap_2 mb_2 mt_2">
          <Input placeholder="Enter key" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
          <Button onClick={handleAddKey}>+ Add Body Param</Button>
        </div>

        <div className="mt_4">
          <ul className="d_flex flex_column gap_2">
            {Object.keys(currentParams).map((key) => (
              <li key={key} className="d_flex align_center gap_2">
                <Input
                  value={editedKeys[key] ?? key}
                  onChange={(e) => handleKeyChange(key, e.target.value)}
                  onBlur={() => handleKeyBlur(key)}
                  className="flex_1"
                />
                <MaterialIcon
                  name="delete"
                  className="cursor_pointer"
                  style={{
                    color: "#dc3545",
                    fontSize: "20px"
                  }}
                  onClick={() => handleDeleteKey(key)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="w_50 d_flex justify_center">
        <div className="d_flex flex_column gap_2 w_75">
          <div className="position_relative p_3 border_radius_medium border_full">
            <pre className="whitespace_pre_wrap m_0">{JSON.stringify(currentParams, null, 2)}</pre>
            <div onClick={handleCopy}>
              <MaterialIcon
                name={copied ? "check" : "content_copy"}
                className="position_absolute cursor_pointer"
                style={{ top: "5px", right: "5px" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AuthenticationTab({ id, selectedApiData, handleInputChange }) {
  return (
    <div className="mt_2 d_flex flex_column gap_2 w_100">
      <div className="d_flex justify_between align_center py_3 border_bottom">
        <p>Enable Zino Authentication</p>
        <Toggle
          value={selectedApiData?.zino_auth ?? false}
          onChange={(e) => handleInputChange("zino_auth", e.target.checked)}
        />
      </div>
      <div className="mt_2 w_100">
        <p className="mb_1">API Key</p>
        <div className="d_flex align_center gap_2 mb_1 w_100">
          <WebhookAPIKeyGeneratorLatest
            id={id}
            apiKey={selectedApiData.api_keys}
            handleInputChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  );
}

export function HeadersTab({ selectedApiData, handleInputChange }) {
  const [newKey, setNewKey] = useState("");
  const [editedKeys, setEditedKeys] = useState({});

  const currentParams = selectedApiData.headers || [];

  const handleAddKey = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || currentParams.includes(trimmedKey)) return;

    const updatedParams = [...currentParams, trimmedKey];
    handleInputChange("headers", updatedParams);
    setNewKey("");
  };

  const handleKeyChange = (oldKey, newKey) => {
    setEditedKeys((prev) => ({ ...prev, [oldKey]: newKey }));
  };

  const handleKeyBlur = (oldKey) => {
    const newKey = editedKeys[oldKey]?.trim();
    if (!newKey || newKey === oldKey || currentParams.includes(newKey)) {
      setEditedKeys((prev) => {
        const copy = { ...prev };
        delete copy[oldKey];
        return copy;
      });
      return;
    }

    const updatedParams = currentParams.map((key) => (key === oldKey ? newKey : key));

    handleInputChange("headers", updatedParams);
    setEditedKeys((prev) => {
      const copy = { ...prev };
      delete copy[oldKey];
      return copy;
    });
  };

  const handleDeleteKey = (keyToDelete) => {
    const updatedParams = currentParams.filter((key) => key !== keyToDelete);
    handleInputChange("headers", updatedParams);
  };

  return (
    <div className="w_50 mt_3">
      <div className="d_flex gap_2 mb_2 mt_2">
        <Input placeholder="Enter key" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
        <Button onClick={handleAddKey}>+ Add Header</Button>
      </div>

      <div className="mt_4">
        <ul className="d_flex flex_column gap_2">
          {currentParams.map((key) => (
            <li key={key} className="d_flex align_center gap_2">
              <Input
                value={editedKeys[key] ?? key}
                onChange={(e) => handleKeyChange(key, e.target.value)}
                onBlur={() => handleKeyBlur(key)}
                className="flex_1"
              />
              <MaterialIcon
                name="delete"
                className="cursor_pointer"
                style={{
                  color: "#dc3545",
                  fontSize: "20px"
                }}
                onClick={() => handleDeleteKey(key)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ActionTab({ selectedApiData, handleInputChange }) {
  const [allJobs, setAllJobs] = useState({});

  useEffect(() => {
    const fetchAllTemplatesAndJobs = async () => {
      try {
        const { data: postJobData } = await axiosInstance.get(`/jobs/filter?places=post_activity`);

        setAllJobs({
          post_activity: postJobData
        });
      } catch (error) {
        handleApiError(error);
      }
    };
    fetchAllTemplatesAndJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setJobTriggerData = (data) => {
    handleInputChange("job_configs", data);
  };

  const setJobTriggerFormula = (data) => {
    handleInputChange("job_trigger_formula", data);
  };

  return (
    <div className="w_100 sm_inner_left ml_auto" style={{ alignItems: "flex-start", paddingLeft: "0px" }}>
      <ActivityJobTrigger
        jobTriggerFormula={selectedApiData?.job_trigger_formula || ""}
        setJobTriggerFormula={setJobTriggerFormula}
        jobTriggerData={selectedApiData?.job_configs || []}
        setJobTriggerData={setJobTriggerData}
        stateid={selectedApiData?.sm_id || ""}
        allJobs={allJobs.post_activity || []}
        actId={""}
        pageType="statemachine"
        jobType="post"
      />
    </div>
  );
}

export function SuccessTab({ selectedApiData, handleInputChange }) {
  const [copied, setCopied] = useState(false);

  const currentSuccess = selectedApiData.success || { status: "200", message: "" };

  const statusCodes = [
    { value: "200", label: "200 - OK" },
    { value: "201", label: "201 - Created" },
    { value: "202", label: "202 - Accepted" },
    { value: "203", label: "203 - Non-Authoritative Information" },
    { value: "204", label: "204 - No Content" },
    { value: "205", label: "205 - Reset Content" },
    { value: "206", label: "206 - Partial Content" },
    { value: "207", label: "207 - Multi-Status" },
    { value: "208", label: "208 - Already Reported" },
    { value: "226", label: "226 - IM Used" }
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(currentSuccess, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleStatusChange = (status) => {
    const updatedSuccess = {
      ...currentSuccess,
      status
    };
    handleInputChange("success", updatedSuccess);
  };

  const handleMessageChange = (message) => {
    const updatedSuccess = {
      ...currentSuccess,
      message
    };
    handleInputChange("success", updatedSuccess);
  };

  return (
    <div className="w_100 d_flex gap_2 mt_3">
      <div className="w_50">
        <div className="d_flex flex_column gap_3 mt_2">
          <div className="d_flex flex_column gap_1">
            <label className="font_weight_medium">Status Code</label>
            <select
              value={currentSuccess.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="builder_input"
            >
              {statusCodes.map((code) => (
                <option key={code.value} value={code.value}>
                  {code.label}
                </option>
              ))}
            </select>
          </div>

          <div className="d_flex flex_column gap_1">
            <label className="font_weight_medium">Message</label>
            <textarea
              placeholder="Enter success message"
              value={currentSuccess.message}
              onChange={(e) => handleMessageChange(e.target.value)}
              className="p_2 border_radius_small border_full"
              rows={6}
              style={{ resize: "vertical" }}
            />
          </div>
        </div>
      </div>

      <div className="w_50 d_flex justify_center">
        <div className="d_flex flex_column gap_2 w_75">
          <div className="position_relative p_3 border_radius_medium border_full">
            <pre className="whitespace_pre_wrap m_0">{JSON.stringify(currentSuccess, null, 2)}</pre>
            <div onClick={handleCopy}>
              <MaterialIcon
                name={copied ? "check" : "content_copy"}
                className="position_absolute cursor_pointer"
                style={{
                  top: "5px",
                  right: "5px",
                  color: copied ? "#28a745" : "#6c757d"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ErrorTab({ selectedApiData, handleInputChange }) {
  const [copied, setCopied] = useState(false);

  const currentError = selectedApiData.error || { status: "400", message: "" };

  const statusCodes = [
    { value: "400", label: "400 - Bad Request" },
    { value: "401", label: "401 - Unauthorized" },
    { value: "402", label: "402 - Payment Required" },
    { value: "403", label: "403 - Forbidden" },
    { value: "404", label: "404 - Not Found" },
    { value: "405", label: "405 - Method Not Allowed" },
    { value: "406", label: "406 - Not Acceptable" },
    { value: "407", label: "407 - Proxy Authentication Required" },
    { value: "408", label: "408 - Request Timeout" },
    { value: "409", label: "409 - Conflict" },
    { value: "410", label: "410 - Gone" },
    { value: "411", label: "411 - Length Required" },
    { value: "412", label: "412 - Precondition Failed" },
    { value: "413", label: "413 - Payload Too Large" },
    { value: "414", label: "414 - URI Too Long" },
    { value: "415", label: "415 - Unsupported Media Type" },
    { value: "416", label: "416 - Range Not Satisfiable" },
    { value: "417", label: "417 - Expectation Failed" },
    { value: "418", label: "418 - I'm a teapot" },
    { value: "421", label: "421 - Misdirected Request" },
    { value: "422", label: "422 - Unprocessable Entity" },
    { value: "423", label: "423 - Locked" },
    { value: "424", label: "424 - Failed Dependency" },
    { value: "425", label: "425 - Too Early" },
    { value: "426", label: "426 - Upgrade Required" },
    { value: "428", label: "428 - Precondition Required" },
    { value: "429", label: "429 - Too Many Requests" },
    { value: "431", label: "431 - Request Header Fields Too Large" },
    { value: "451", label: "451 - Unavailable For Legal Reasons" }
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(currentError, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleStatusChange = (status) => {
    const updatedError = {
      ...currentError,
      status
    };
    handleInputChange("error", updatedError);
  };

  const handleMessageChange = (message) => {
    const updatedError = {
      ...currentError,
      message
    };
    handleInputChange("error", updatedError);
  };

  return (
    <div className="w_100 d_flex gap_2 mt_3">
      <div className="w_50">
        <div className="d_flex flex_column gap_3 mt_2">
          <div className="d_flex flex_column gap_1">
            <label className="font_weight_medium">Status Code</label>
            <select
              value={currentError.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="builder_input"
            >
              {statusCodes.map((code) => (
                <option key={code.value} value={code.value}>
                  {code.label}
                </option>
              ))}
            </select>
          </div>

          <div className="d_flex flex_column gap_1">
            <label className="font_weight_medium">Message</label>
            <textarea
              placeholder="Enter error message"
              value={currentError.message}
              onChange={(e) => handleMessageChange(e.target.value)}
              className="p_2 border_radius_small border_full"
              rows={6}
              style={{ resize: "vertical" }}
            />
          </div>
        </div>
      </div>

      <div className="w_50 d_flex justify_center">
        <div className="d_flex flex_column gap_2 w_75">
          <div className="position_relative p_3 border_radius_medium border_full">
            <pre className="whitespace_pre_wrap m_0">{JSON.stringify(currentError, null, 2)}</pre>
            <div onClick={handleCopy}>
              <MaterialIcon
                name={copied ? "check" : "content_copy"}
                className="position_absolute cursor_pointer"
                style={{
                  top: "5px",
                  right: "5px",
                  color: copied ? "#28a745" : "#6c757d"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
